import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { useAuth } from '@/hooks/useAuth';
import { ServiceStatusManager } from '@/components/admin/status/ServiceStatusManager';
import { IncidentManager } from '@/components/admin/status/IncidentManager';
import { FeatureRequestManager } from '@/components/admin/status/FeatureRequestManager';
import { ChangelogManager } from '@/components/admin/status/ChangelogManager';

/**
 * Status Admin Page
 *
 * Super admin interface for managing system status, incidents, feature requests, and changelog
 */

type StatusTab = 'services' | 'incidents' | 'requests' | 'changelog';

export const StatusAdmin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState<StatusTab>(() => {
    const tabParam = searchParams.get('tab') as StatusTab | null;
    if (tabParam && ['services', 'incidents', 'requests', 'changelog'].includes(tabParam)) {
      return tabParam;
    }
    return 'services';
  });

  const handleTabChange = useCallback(
    (tab: StatusTab) => {
      setActiveTab(tab);
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', tab);
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Super admin check (route is already guarded by RequireSuperAdmin, this is
  // redundant defense-in-depth). Must run AFTER all hooks above per Rules of Hooks.
  useEffect(() => {
    if (!user?.isSuperAdmin) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  if (!user?.isSuperAdmin) {
    return null;
  }

  const tabs = [
    { id: 'services' as const, label: 'Services', icon: ICONS.PULSE },
    { id: 'incidents' as const, label: 'Incidents', icon: ICONS.CAUTION },
    { id: 'requests' as const, label: 'Feature Requests', icon: ICONS.BULB },
    { id: 'changelog' as const, label: 'Changelog', icon: ICONS.FILE },
  ];

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <AdminPageHeader
        title="Status Management"
        subtitle="Manage system status, incidents, and user feedback"
        documentTitle="Admin - Status"
      />

      {/* Mobile: Icon grid */}
      <div className="lg:hidden bg-gray-100/80 backdrop-blur-sm p-2 rounded-2xl">
        <div className="grid grid-cols-4 gap-1">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={cn(
                  'group flex flex-col items-center justify-center gap-1 px-2 py-2.5 rounded-xl transition-all duration-200',
                  isActive ? 'bg-white shadow-sm' : 'hover:bg-white/50'
                )}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon
                  name={tab.icon}
                  size="sm"
                  color={isActive ? 'text-primary-600' : 'text-gray-500'}
                  ariaHidden
                />
                <span className={cn(
                  'text-[10px] font-medium leading-tight truncate max-w-full',
                  isActive ? 'text-gray-900' : 'text-gray-500'
                )}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Desktop: pill tabs */}
      <div className="hidden lg:flex bg-gray-100/80 backdrop-blur-sm p-1.5 rounded-2xl gap-1 flex-wrap">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={cn(
              'group inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 whitespace-nowrap',
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
            )}
            aria-current={activeTab === tab.id ? 'page' : undefined}
          >
            <Icon
              name={tab.icon}
              size="sm"
              color={activeTab === tab.id ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-600'}
              ariaHidden
            />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'services' && <ServiceStatusManager />}
      {activeTab === 'incidents' && <IncidentManager />}
      {activeTab === 'requests' && <FeatureRequestManager />}
      {activeTab === 'changelog' && <ChangelogManager />}
    </div>
  );
};
